import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../../components/Logo/Logo';
import { 
  CreditCard, 
  FileText, 
  Database, 
  BarChart3, 
  Shield, 
  Zap,
  Users,
  Building2,
  Calculator,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Lock,
  Globe,
  Target,
  Award
} from 'lucide-react';

const Features = () => {
  const mainFeatures = [
    {
      icon: CreditCard,
      title: 'Chargeback Management',
      description: 'Streamline pharmaceutical chargeback processing with automated validation and EDI 844 compliance.',
      features: [
        'Automated chargeback validation',
        'EDI 844/845/849 integration',
        'Real-time status tracking',
        'Batch processing capabilities',
        'Exception handling and alerts',
        'Comprehensive audit trails'
      ]
    },
    {
      icon: FileText,
      title: 'Contract Management',
      description: 'Manage complex pharmaceutical contracts with GPO integration and automated pricing rules.',
      features: [
        'Contract lifecycle management',
        'GPO integration and tracking',
        'Automated pricing calculations',
        'Renewal and expiration alerts',
        'Document management',
        'Compliance monitoring'
      ]
    },
    {
      icon: Database,
      title: 'Sales Data Analytics',
      description: 'Comprehensive sales data analysis with AMP calculations and compliance reporting.',
      features: [
        'Real-time sales data processing',
        'AMP calculation engine',
        'Trend analysis and forecasting',
        'Custom reporting dashboards',
        'Data visualization tools',
        'Export capabilities'
      ]
    },
    {
      icon: BarChart3,
      title: 'Advanced Reporting',
      description: 'Generate comprehensive reports and analytics for better business decision making.',
      features: [
        'Custom report builder',
        'Scheduled report delivery',
        'Interactive dashboards',
        'KPI tracking and alerts',
        'Data export in multiple formats',
        'Role-based access controls'
      ]
    },
    {
      icon: Shield,
      title: 'Security & Compliance',
      description: 'Enterprise-grade security with HIPAA compliance and comprehensive audit trails.',
      features: [
        'HIPAA compliant infrastructure',
        'SOC 2 Type II certified',
        'End-to-end encryption',
        'Role-based access control',
        'Comprehensive audit logs',
        'Data backup and recovery'
      ]
    },
    {
      icon: Zap,
      title: 'EDI Integration',
      description: 'Seamless EDI integration for automated chargeback processing and data exchange.',
      features: [
        'EDI 844/845/849 support',
        'Automated data mapping',
        'Real-time validation',
        'Error handling and retry logic',
        'Trading partner management',
        'Compliance monitoring'
      ]
    }
  ];

  const additionalFeatures = [
    {
      icon: Users,
      title: 'User Management',
      description: 'Comprehensive user management with role-based access and permissions.'
    },
    {
      icon: Building2,
      title: 'GPO Management',
      description: 'Manage Group Purchasing Organizations and their relationships with contracts.'
    },
    {
      icon: Calculator,
      title: 'AMP Calculations',
      description: 'Automated Average Manufacturer Price calculations for compliance reporting.'
    },
    {
      icon: TrendingUp,
      title: 'Performance Analytics',
      description: 'Track system performance and user activity with detailed analytics.'
    },
    {
      icon: Target,
      title: 'Workflow Automation',
      description: 'Automate repetitive tasks and streamline business processes.'
    },
    {
      icon: Award,
      title: 'Industry Compliance',
      description: 'Stay compliant with FDA, HIPAA, and other industry regulations.'
    }
  ];

  const benefits = [
    {
      title: 'Reduce Processing Time',
      description: 'Cut chargeback processing time by up to 80% with automated workflows.',
      percentage: '80%'
    },
    {
      title: 'Improve Accuracy',
      description: 'Eliminate manual errors with automated validation and data processing.',
      percentage: '99.9%'
    },
    {
      title: 'Enhance Compliance',
      description: 'Ensure full compliance with industry regulations and standards.',
      percentage: '100%'
    },
    {
      title: 'Increase Efficiency',
      description: 'Streamline operations and improve team productivity significantly.',
      percentage: '60%'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Link to="/">
                <Logo variant="color" size="lg" />
              </Link>
            </div>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-500 hover:text-gray-900">Home</Link>
              <Link to="/about" className="text-gray-500 hover:text-gray-900">About</Link>
              <Link to="/contact" className="text-gray-500 hover:text-gray-900">Contact</Link>
              <Link to="/login" className="text-gray-500 hover:text-gray-900">Sign In</Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Powerful Features
            </h1>
            <p className="text-xl md:text-2xl text-primary-100 max-w-3xl mx-auto">
              Everything you need to streamline pharmaceutical chargeback management 
              and ensure compliance in one integrated platform.
            </p>
          </div>
        </div>
      </section>

      {/* Main Features Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Core Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive tools designed specifically for pharmaceutical companies 
              and wholesalers.
            </p>
          </div>
          <div className="space-y-24">
            {mainFeatures.map((feature, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
              }`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center mb-6">
                    <div className="bg-primary-100 p-4 rounded-lg mr-4">
                      <feature.icon className="h-8 w-8 text-primary-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{feature.title}</h3>
                  </div>
                  <p className="text-lg text-gray-600 mb-6">{feature.description}</p>
                  <ul className="space-y-3">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <div className="bg-gray-100 rounded-lg p-8 h-64 flex items-center justify-center">
                    <feature.icon className="h-32 w-32 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Features Section */}
      <section className="bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Additional Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              More tools and capabilities to enhance your pharmaceutical operations.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {additionalFeatures.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="bg-primary-100 p-3 rounded-lg mr-4">
                    <feature.icon className="h-6 w-6 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{feature.title}</h3>
                </div>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Proven Benefits
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See the measurable impact on your pharmaceutical operations.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="bg-primary-600 text-white rounded-full w-24 h-24 mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl font-bold">{benefit.percentage}</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security & Compliance Section */}
      <section className="bg-primary-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Enterprise-Grade Security
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Your data security is our top priority. We maintain the highest 
                standards of security and compliance.
              </p>
              <div className="space-y-6">
                <div className="flex items-center">
                  <Shield className="h-6 w-6 text-primary-600 mr-4" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">HIPAA Compliant</h3>
                    <p className="text-gray-600">Full compliance with healthcare data protection standards</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Lock className="h-6 w-6 text-primary-600 mr-4" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">SOC 2 Type II</h3>
                    <p className="text-gray-600">Certified security controls and processes</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Globe className="h-6 w-6 text-primary-600 mr-4" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Global Standards</h3>
                    <p className="text-gray-600">Compliant with international data protection regulations</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Compliance Certifications</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="font-medium">HIPAA Compliance</span>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="font-medium">SOC 2 Type II</span>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="font-medium">ISO 27001</span>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="font-medium">GDPR Ready</span>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-600 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Experience These Features?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Start your free trial today and see how PharmaChargeback can transform 
            your pharmaceutical operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
            >
              Start Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-primary-600 transition-colors inline-flex items-center justify-center"
            >
              Schedule Demo
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <Logo variant="color" size="lg" className="mb-4" />
              <p className="text-gray-400 mb-4">
                Streamlining pharmaceutical chargeback management for the modern era.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/features" className="text-gray-400 hover:text-white">Features</Link></li>
                <li><Link to="/pricing" className="text-gray-400 hover:text-white">Pricing</Link></li>
                <li><Link to="/demo" className="text-gray-400 hover:text-white">Demo</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-white">About Us</Link></li>
                <li><Link to="/careers" className="text-gray-400 hover:text-white">Careers</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><Link to="/contact" className="text-gray-400 hover:text-white">Contact Us</Link></li>
                <li><Link to="/privacy" className="text-gray-400 hover:text-white">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2024 PharmaChargeback. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Features;
